import { Box, FormControl, InputLabel, MenuItem, Select, Tab, Tabs, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DashboardYearFilter } from '../components/DashboardYearFilter';
import { useEnrollmentByAdvisorData } from '../contexts';
import { useDashboardSchoolYear } from '../contexts/DashboardSchoolYearContext';
import { pillToggleGroupStyles } from '../styles/segmentedControlStyles';

export type EnrollmentPeriod = 'year' | 'month';

interface Props {
    period: EnrollmentPeriod;
    onPeriodChange: (period: EnrollmentPeriod) => void;
    month: number | null;
    onMonthChange: (month: number | null) => void;
}

export const EnrollmentByAdvisorControls = ({ period, onPeriodChange, month, onMonthChange }: Props) => {
    const { filter, updateFilter, activeSchoolYear } = useEnrollmentByAdvisorData();
    const { schoolYears } = useDashboardSchoolYear();

    return <DashboardYearFilter
        idPrefix="enrollment-by-advisor"
        years={filter.schoolYears}
        activeSchoolYear={activeSchoolYear}
        availableYears={schoolYears.map(schoolYear => schoolYear.id)}
        onApply={schoolYears => updateFilter({ schoolYears })}
        compact
    >
        <ToggleButtonGroup
            value={filter.activeStudentsOnly ? 'active' : 'all'}
            exclusive
            size="small"
            aria-label="Student enrollment scope"
            onChange={(_, value: string | null) => value && updateFilter({ activeStudentsOnly: value === 'active' })}
            sx={pillToggleGroupStyles}
        >
            <ToggleButton value="all">All assigned</ToggleButton>
            <ToggleButton value="active" title="Latest enrollment without an end date in the current school year">Active students</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, width: '100%' }}>
            <Tabs
                value={period}
                aria-label="Enrollment period"
                onChange={(_, value: EnrollmentPeriod) => onPeriodChange(value)}
                sx={{ flexShrink: 0, '& .MuiTabs-flexContainer': { justifyContent: 'flex-start' } }}
            >
                <Tab label="Year" value="year" />
                <Tab label="Month" value="month" />
            </Tabs>
            {period === 'month' && <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="enrollment-by-advisor-month-label">Month</InputLabel>
                <Select
                    labelId="enrollment-by-advisor-month-label"
                    label="Month"
                    value={month ?? ''}
                    onChange={event => onMonthChange(event.target.value === '' ? null : Number(event.target.value))}
                >
                    <MenuItem value="">All Months</MenuItem>
                    {Array.from({ length: 12 }, (_, index) => index + 1).map(monthNumber =>
                        <MenuItem key={monthNumber} value={monthNumber}>
                            {new Date(2000, monthNumber - 1).toLocaleString(undefined, { month: 'long' })}
                        </MenuItem>
                    )}
                </Select>
            </FormControl>}
        </Box>
    </DashboardYearFilter>;
};
